import { useEffect } from "react";
import { useParams } from "react-router-dom";

import { clearCachedInferenceResults } from "@rapid/utils/api/datasets";
import { useWorkflowByUrl } from "@rapid/queries/workflowQueries";
import { useWorkflowContext } from "@rapid/contexts/WorkflowContext";
import { useCurrentWorkspace } from "@rapid/hooks/useCurrentWorkspace";
import { useClearMediaState, useSetCurrentMedia } from "@rapid/store";
import LoadingScreen from "@rapid/components/LoadingScreen";
import { ErrorInfo } from "@rapid/components/ErrorInfo";
import { useMedia } from "@rapid/contexts/MediaContext";
import { useAuth } from "@rapid/contexts/AuthContext";

export const HydrateWorkflow = ({ children }: { children: React.ReactNode }) => {
    const { workflowUrl } = useParams<{ workflowUrl: string }>();
    const { currentWorkspace } = useCurrentWorkspace();
    const { activeSession } = useAuth();
    const { data, isLoading, error, refetch } = useWorkflowByUrl(
        workflowUrl,
        currentWorkspace?.id,
        activeSession
    );
    const { currentWorkflow, setCurrentWorkflow } = useWorkflowContext();

    const clearMediaState = useClearMediaState();
    const setCurrentMedia = useSetCurrentMedia();
    const { setMediaItemIds, mediaItems } = useMedia();

    useEffect(() => {
        setCurrentWorkflow(data ?? null);
        return () => setCurrentWorkflow(null);
    }, [data, setCurrentWorkflow]);

    // Clear media when workflow URL doesn't match current workflow
    useEffect(() => {
        if (mediaItems.length > 0 && currentWorkflow?.url !== workflowUrl) {
            clearMediaState();
            setMediaItemIds([]);
            setCurrentMedia(undefined);
            if (currentWorkflow?.url) {
                clearCachedInferenceResults(currentWorkflow.url);
            }
        }
    }, [
        workflowUrl,
        currentWorkflow?.url,
        mediaItems.length,
        clearMediaState,
        setMediaItemIds,
        setCurrentMedia
    ]);

    if (error) {
        return (
            <div className="flex h-full items-center justify-center p-4">
                <ErrorInfo error={error.message} onRetryClick={() => refetch()} />
            </div>
        );
    }

    if (isLoading || !data || currentWorkflow?.url !== workflowUrl) {
        return <LoadingScreen />;
    }

    return children;
};
